/**
 * App Surface root (`/`) — Canvas-as-Primary-Home entry point
 * (VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1 §0/§4/§52/§53).
 *
 * `/` itself carries no deep-link intent (a user who wants a specific
 * page always has that page's own URL, e.g. `/workspace/<id>/images` —
 * RequireAuth's own `state.from` capture handles THAT redirect-after-
 * login case, spec §5, completely independently of this component).
 * This component's only job: resolve auth state, then hand off to
 * exactly ONE further redirect —
 *
 *   not authenticated  -> `/login`
 *   platform admin     -> `/platform`
 *   tenant user         -> `/workspace` (-> WorkspaceIndexRedirect
 *                           -> `/workspace/<id>/home` -> Home Canvas)
 *
 * `/` never appears again anywhere further down this chain, so there is
 * no `/ -> /workspace -> / -> ...` redirect-loop risk (spec §52/§53) —
 * unlike `RequireAuth`, this component is never itself re-entered by a
 * session-invalidation redirect (nothing downstream ever points back at
 * `/`).
 */
import { Spin } from 'antd'
import { Navigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { AuthUnavailable, useEnsureAuthChecked } from './guards'

function FullPageSpin() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin size="large" />
    </div>
  )
}

export default function RootEntryRedirect() {
  const { me } = useAuth()
  const authState = useEnsureAuthChecked()

  if (authState === 'checking') return <FullPageSpin />
  if (authState === 'unavailable') return <AuthUnavailable />
  if (!me) return <Navigate to="/login" replace />
  return <Navigate to={me.is_platform_admin ? '/platform' : '/workspace'} replace />
}
